import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { AppEndpoint } from "app/app.endpoints";
import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { HttpRequestService } from "app/auth/service/http-request.service";
import { AuthenticationService } from "../../auth/service";
import { Observable } from "rxjs";
import { pick } from "lodash";
import { environment } from "environments/environment";
import { catchError, map } from "rxjs/operators";
import { LOCAL_STORAGE_KEYS } from "../shared/enums/LocalStorage.enum";

@Injectable({ providedIn: "root" })
export class UserService {
  private url = AppEndpoint.BASE_URL + "/private" + "/users";
  private baseUrl = environment.apiUrl + "/private" + "/users";

  constructor(
    private httpService: HttpRequestService,
    private toastrService: ToastrService,
    private authService: AuthenticationService,
    private http: HttpClient,
  ) {}

  createUser(user: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/create`, user);
  }

  getAllUsers(): Observable<any> {
    return this.http.get(`${this.baseUrl}/list`);
  }

  getUsersWithFilters(filters = {}): Observable<any> {
    return this.http.get(`${this.url}/list-filters`, { params: filters });
  }

  getUsersByCompany(companyId: number): Observable<any> {
    return this.http.get(`${this.url}/list-by-company/${companyId}`);
  }

  getUsersByOwner(ownerId: string): Observable<any> {
    return this.http.get(`${this.url}/list-by-owner/${ownerId}`);
  }

  getByRole(role: string): Observable<any> {
    return this.httpService
      .UsersByRole(this.authService.companyId)
      .get({ params: { role } });
  }

  getAll3(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .Users2(this.authService.companyId)
        .get()
        .subscribe(
          (response: any) => resolve(response),
          (err) => reject(err),
        );
    });
  }

  getUserById(userId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/${userId}`);
  }

  getAuthData = (): Observable<any> => this.http.get(`${this.url}/auth-data`);

  update(conf, userId): Promise<any> {
    conf.updated_by = this.authService.userId;
    return new Promise((resolve, reject) => {
      this.httpService
        .User(this.authService.companyId, userId)
        .put(conf)
        .subscribe(
          (response: any) => {
            resolve(response);
          },
          (err) => {
            let msg = "No se pudo actualizar el usuario";
            if (err.error.username) msg = "Nombre de usuario ya existe";
            if (err.error.email) msg = "Correo de usuario ya existe";
            this.toastrService.error(msg, " Error", {
              toastClass: "toast ngx-toastr",
              closeButton: true,
            });
            reject(err);
          },
        );
    });
  }

  updateUser(user: any, userId: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/${userId}`, user);
  }
  
  updateUserProfile(user: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/update-profile`, user);
  }

  requestPasswordChange = (userId: number): Observable<any> =>
    this.http.put(`${this.baseUrl}/request-password-change/${userId}`, {}).pipe(
      catchError((error: any) => {
        this.toastrService.error(
          "Ocurrio un error con la peticion de cambio de contraseña",
        );
        console.error("Request password change catch error", error);
        throw error;
      }),
    );

  lockUnlockUser = (userId: number): Observable<any> =>
    this.http.put(`${this.baseUrl}/lock-unlock/${userId}`, {}).pipe(
      catchError((error: any) => {
        this.toastrService.error(
          "Ocurrio un error bloquear/desbloquear usuario",
        );
        console.error("Lock Unlock company catch error", error);
        throw error;
      }),
    );

  getLimits = (): Observable<any> =>
    this.http.get(`${this.baseUrl}/get-limits/user`).pipe(
      catchError((error: any) => {
        this.toastrService.error("Ocurrio un error obteniendo limites");
        console.error("Get limits catch error", error);
        throw error;
      }),
    );

  updateLimit = (userId: number, limits: any): Observable<any> =>
    this.http.put(`${this.baseUrl}/update-limit/${userId}`, limits).pipe(
      catchError((error: any) => {
        this.toastrService.error("Ocurrio un error actualizando límites");
        console.error("Update limits catch error", error);
        throw error;
      }),
    );

  deleteLimit = (limitId: number): Observable<any> =>
    this.http.delete(`${this.baseUrl}/delete-limit/${limitId}`).pipe(
      catchError((error: any) => {
        this.toastrService.error("Ocurrio un error elimnando el límite");
        console.error("Delete limit catch error", error);
        throw error;
      }),
    );

  comparePassword = (data: any = {}): Observable<any> =>
    this.httpService
      .UserPassword(this.authService.companyId, "compare-pass")
      .get({ params: { ...data } });

  changePassword = (data: any = {}): Observable<any> =>
    this.http.put(`${this.baseUrl}/change-password`, data);

  changePasswordFromProfile = (data: any = {}): Observable<any> =>
    this.httpService
      .UserPassword(this.authService.companyId, "change-pass-profile")
      .put(data);

  authVerify = () =>
    this.getAuthData().pipe(
      map((authData) => {
        console.log(authData);
        this.authService.setLoggedUserData(authData);
      }),
    );

  switchSession = (userId: string) => {
    return this.http.post(`${this.url}/switch-session/${userId}`, {}).pipe(
      map((res) => {
        console.log("NEW TOKEN: ", res);
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.SHARED_TOKEN,
          this.authService.token,
        );
        localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, "FAKE");
      }),
      catchError((error: HttpErrorResponse) => {
        console.error("Error al cambiar de sesión: ", error);
        this.toastrService.error("Error al cambiar sesión");
        throw error;
      }),
    );
  };

  getApiBcrStatus(userId: number): Observable<{
    canUseBcrApi: boolean;
    defaultSenderId: number;
  }> {
    return this.http.get<{
      canUseBcrApi: boolean;
      defaultSenderId: number;
    }>(`${this.baseUrl}/bcr-api-access/${userId}/status`);
  }

  getSendersForBcrApiAccess(userId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/bcr-api-access/${userId}/senders`,
    );
  }

  linkSenderToUserBcrApi(
    userId: number,
    senderId: number | null,
  ): Observable<any> {
    return this.http.post(`${this.baseUrl}/bcr-api-access/link-sender`, {
      userId,
      senderId,
    });
  }

  get companyId() {
    return this.authService.companyId;
  }

  get userId() {
    return this.authService.userId;
  }
}
